import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Application,
  ApplicationStatus,
  DatePeriod,
} from '@features/applications';
import ApplicationListItem from '@features/applications/components/ApplicationListItem/ApplicationListItem';
import ApplicationViewModal from '@features/applications/components/ApplicationViewModal/ApplicationViewModal';
import BulkActions from '@features/applications/components/BulkActions/BulkActions';
import { white } from '@styles/colors';
import { authContext, Position } from '@staffedupusa/shared-components';
import EnterpriseApplicationViewModal from '../EnterpriseApplicationViewModal/EnterpriseApplicationViewModal';
import TagsWotcFilters from '../TagsWotcFilters/TagsWotcFilters';
import MyTeamBulkActions from '@features/employerTeam/components/MyTeamBulkActions';

type ApplicationsListProps = {
  applications: Application[];
  allApplicationsIds: number[];
  isLoading: boolean;
  isWOTCOnly: boolean;
  setIsWOTCOnly: Dispatch<SetStateAction<boolean>>;
  alertText: string;
  disableButtons: boolean;
  defaultOnboarding?: boolean;
  searchValue: string;
  hiredAction?: string;
  hideSelect?: boolean;
  isFilterSelected?: boolean;
  showSearchBox?: boolean;
  showFilterByDate?: boolean;
  setSearchValue?: Dispatch<SetStateAction<'All' | string>>;
  selectedDatePeriod?: DatePeriod;
  setSelectedDatePeriod?: Dispatch<SetStateAction<DatePeriod>>;
  setTriggerChange?: Dispatch<SetStateAction<boolean>>;
  triggerChange?: boolean;
  showTeamBulk?: boolean;
  hideFilters?: boolean;
  openCustomDateModal?: () => void;
  selectedTag: string;
  handleTagsChange: (event: {
    target: { value: React.SetStateAction<string> };
  }) => void;
  handlePageNo: (newPageNo: number) => void;
  pageNo: number;
  selectedStatus?: ApplicationStatus | string;
  totalAplications?: number;
  localJobPostings?: Position[];
};

const ApplicationsList: FC<ApplicationsListProps> = ({
  applications,
  isLoading,
  isWOTCOnly,
  isFilterSelected,
  setIsWOTCOnly,
  setTriggerChange,
  triggerChange,
  showTeamBulk,
  defaultOnboarding,
  alertText,
  disableButtons,
  hideFilters,
  searchValue,
  hideSelect = false,
  showSearchBox = false,
  showFilterByDate = false,
  setSearchValue = () => {},
  selectedDatePeriod = 'all time',
  setSelectedDatePeriod = () => {},
  openCustomDateModal = () => {},
  selectedTag,
  handleTagsChange,
  handlePageNo,
  pageNo,
  hiredAction,
  totalAplications,
  allApplicationsIds,
  selectedStatus,
  localJobPostings,
}) => {
  const { state } = useLocation();
  const containerRef = useRef<HTMLUListElement | null>(null);
  const { authData } = useContext(authContext);
  const navigate = useNavigate();
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<
    number[]
  >([]);
  const [unselectedApplications, setUnselectedApplications] = useState<
    number[]
  >([]);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [applicationData, setApplicationData] = useState<Application[] | null>(
    null,
  );
  const [showSelectAll, setShowSelectAll] = useState(false);
  const [showSelectFirstAll, setShowSelectFirstAll] = useState(false);

  const handleToggleAllPositions = () => {
    if (applicationData)
      if (
        selectedApplicationIds.length === allApplicationsIds?.length &&
        allApplicationsIds?.length !== 0
      ) {
        setShowSelectAll(false);
        setShowSelectFirstAll(false);
        setSelectedApplicationIds([]);
      } else if (allApplicationsIds?.length !== 0) {
        setShowSelectAll(true);
        setShowSelectFirstAll(true);
        setSelectedApplicationIds([...allApplicationsIds]);
      } else if (
        applicationData &&
        applicationData?.length > 0 &&
        !showSelectFirstAll
      ) {
        setShowSelectAll(false);
        setShowSelectFirstAll(true);
        setSelectedApplicationIds(applicationData?.map((item) => item.id));
      } else {
        setShowSelectAll(false);
        setShowSelectFirstAll(false);
        setSelectedApplicationIds([]);
      }
  };

  function throttle(func: (...args: any[]) => void, limit: number) {
    let lastFunc: ReturnType<typeof setTimeout>;
    let lastRan: number;
    return (...args: any[]) => {
      if (!lastRan) {
        func(...args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(
          () => {
            if (Date.now() - lastRan >= limit) {
              func(...args);
              lastRan = Date.now();
            }
          },
          limit - (Date.now() - lastRan),
        );
      }
    };
  }

  const handleReset = () => {
    setSelectedApplicationIds([]);
    setUnselectedApplications([]);
    handlePageNo(1);
  };

  const updateRating = (id: number, newRating: number) => {
    if (applicationData && applicationData.length > 0) {
      setApplicationData(
        (prevApps) =>
          prevApps &&
          prevApps.map((app) =>
            app.id === id ? { ...app, rating: newRating } : app,
          ),
      );
    }
    if (selectedApplication && selectedApplication.id === id) {
      setSelectedApplication((prevApp) =>
        prevApp ? { ...prevApp, rating: newRating } : null,
      );
    }
  };

  useEffect(() => {
    if (triggerChange) {
      handlePageNo(1);
      setApplicationData(null);
      setTriggerChange && setTriggerChange(false);
      setSelectedApplicationIds([]);
      setUnselectedApplications([]);
      setShowSelectFirstAll(false);
      setShowSelectAll(false);
    }
  }, [triggerChange]);

  useEffect(() => {
    if (applications && applications?.length > 0 && !isLoading) {
      const handleScroll = () => {
        if (
          containerRef.current &&
          window.innerHeight + window.scrollY >=
            containerRef.current.scrollHeight
        ) {
          if (applications.length === 10) {
            handlePageNo(pageNo + 1);
          }
        }
      };
      if (pageNo === 1) {
        setApplicationData([...applications]);
      } else if (pageNo > 1) {
        setApplicationData((pre) => [...(pre || []), ...applications]);
      }

      const throttledScroll = throttle(handleScroll, 200);
      window.addEventListener('scroll', throttledScroll);

      return () => {
        window.removeEventListener('scroll', throttledScroll);
      };
    } else if (
      applications &&
      applications?.length == 0 &&
      !isLoading &&
      pageNo === 1
    ) {
      setApplicationData([]);
    }
  }, [applications, isLoading]);

  useEffect(() => {
    if (selectedApplication) {
      const updatedSelectedApplication = applications.find(
        (application) => application.id === selectedApplication?.id,
      );

      if (updatedSelectedApplication) {
        setSelectedApplication(updatedSelectedApplication);
      }
    }

    if (
      showSelectAll &&
      allApplicationsIds &&
      allApplicationsIds?.length > 0 &&
      showSelectFirstAll
    ) {
      setSelectedApplicationIds([...allApplicationsIds]);
    } else if (
      allApplicationsIds &&
      allApplicationsIds?.length > 0 &&
      showSelectFirstAll
    ) {
      const selectIds = allApplicationsIds?.filter(
        (id) => !unselectedApplications?.includes(id),
      );
      setSelectedApplicationIds([...selectIds]);
    }
  }, [applications]);

  useEffect(() => {
    if (state?.application) {
      setSelectedApplication(JSON.parse(state.application));
    }
  }, [state]);
  return (
    <Stack spacing={3} mt={!hideSelect ? 3 : 0}>
      {alertText && <Alert severity="error">{alertText}</Alert>}
      {!hideFilters && (
        <Grid
          container
          columnSpacing={2}
          rowSpacing={2}
          bgcolor={white}
          borderRadius="8px"
          p={3}
          alignItems="center"
          maxWidth="100%"
        >
          {!hideSelect && (
            <>
              <Grid item xs={12} sm={6} lg={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        selectedApplicationIds.length ===
                        (allApplicationsIds?.length !== 0
                          ? allApplicationsIds?.length
                          : applicationData && applicationData?.length)
                      }
                      indeterminate={
                        selectedApplicationIds.length > 0 &&
                        selectedApplicationIds.length <
                        (allApplicationsIds?.length ?? 0)
                      }
                      onChange={handleToggleAllPositions}
                      disabled={
                        disableButtons ||
                        !Boolean(!applicationData || applicationData?.length > 0)
                      }
                    />
                  }
                  label={`Select All ${totalAplications && totalAplications > 0 ? totalAplications : applicationData && applicationData?.length > 0 ? applicationData?.length : ''}`}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={3}>
                <BulkActions
                  disabled={!selectedApplicationIds.length || disableButtons}
                  selectedApplicationsIds={selectedApplicationIds}
                  handlePageNo={handleReset}
                  filterData={selectedStatus === 'archived' ? true : false}
                  selectedStatus={selectedStatus}
                />
              </Grid>
            </>
          )}

          {showFilterByDate && (
            <Grid item xs={12} sm={6} lg={4}>
              <Stack spacing={2} direction="row" width="100%">
                <FormControl
                  size="small"
                  variant="outlined"
                  disabled={disableButtons}
                  fullWidth
                >
                  <InputLabel id="date-filter">Filter by Date</InputLabel>
                  <Select
                    size="small"
                    label="Filter by Date"
                    labelId="date-filter"
                    value={selectedDatePeriod}
                    onChange={(e) => {
                      handlePageNo(1);
                      setSelectedDatePeriod(e.target.value as DatePeriod);
                    }}
                  >
                    <MenuItem value="ALL">All time</MenuItem>
                    <MenuItem value="TODAY">Today</MenuItem>
                    <MenuItem value="YESTERDAY">Yesterday</MenuItem>
                    <MenuItem value="WEEK">7 days</MenuItem>
                    <MenuItem value="MONTH">30 days</MenuItem>
                    <MenuItem value="custom date">Custom date</MenuItem>
                  </Select>
                </FormControl>

                {selectedDatePeriod === 'custom date' && (
                  <IconButton onClick={openCustomDateModal}>
                    <CalendarMonthIcon />
                  </IconButton>
                )}
              </Stack>
            </Grid>
          )}

          <Grid width="100%" item xs={12} sm={6} lg={showSearchBox ? 8 : 6}>
            <TextField
              variant="outlined"
              size="small"
              color="secondary"
              fullWidth
              placeholder="Search by Applicant Name&hellip;"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="secondary" sx={{ fontSize: '20px' }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment
                    position="end"
                    onClick={() => {
                      handlePageNo(1);
                      setSearchValue('');
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ClearIcon color="secondary" sx={{ fontSize: '18px' }} />
                  </InputAdornment>
                ) : null,
              }}
              value={searchValue}
              onChange={(e) => {
                handlePageNo(1);
                setSearchValue(e.target.value);
              }}
              sx={{
                '& input': {
                  fontSize: '13px',
                },
              }}
            />
          </Grid>
          {showSearchBox && (
            <>
              <TagsWotcFilters
                disableButtons={disableButtons}
                showSearchBox={showSearchBox}
                isWOTCOnly={isWOTCOnly}
                selectedTag={selectedTag}
                onWotcChange={() => {
                  handlePageNo(1);
                  setIsWOTCOnly(!isWOTCOnly);
                }}
                onTagsChange={(e: SelectChangeEvent<string>) => {
                  handlePageNo(1);
                  handleTagsChange({
                    target: { value: e.target.value as string },
                  });
                }}
              />
            </>
          )}
        </Grid>
      )}
      {showTeamBulk && (
        <MyTeamBulkActions
          localJobPostings={localJobPostings}
          allApplicationsIds={allApplicationsIds}
          setSelectedApplicationIds={setSelectedApplicationIds}
          selectedApplicationIds={selectedApplicationIds}
          handleReset={handleReset}
          hiredAction={hiredAction}
          disabled={disableButtons}
        />
      )}
      <Stack ref={containerRef} spacing={3} component="ul" sx={{ p: 0 }}>
        {isLoading && pageNo === 1 ? (
          <Box textAlign="center">
            <CircularProgress data-testid="loader" color="success" />
          </Box>
        ):(
          <>
          {applicationData && applicationData?.length
            ? applicationData.map((application) => (
                <ApplicationListItem
                  key={application.id}
                  application={application}
                  updateRating={updateRating}
                  selectedApplications={selectedApplicationIds}
                  setSelectedApplications={setSelectedApplicationIds}
                  searchValue={searchValue}
                  setSelectedApplication={setSelectedApplication}
                  hideSelect={hideSelect}
                  setUnselectedApplications={setUnselectedApplications}
                  setShowSelectAll={setShowSelectAll}
                  disableButtons={disableButtons}
                  setApplicationData={setApplicationData}
                />
              ))
            : null}
          </>
        )}
      </Stack>
      {authData.user?.enterprise ? (
        <EnterpriseApplicationViewModal
          handleClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          searchValue={searchValue}
          updateRating={updateRating}
          setApplication={setSelectedApplication}
          selectedCompanyId={selectedApplication?.job_listing?.company?.id}
          setApplicationData={setApplicationData}
          handlePageNo={(newPageNo) => {
            setTriggerChange && setTriggerChange(true);
            handlePageNo(newPageNo);
          }}
        />
      ) : (
        <ApplicationViewModal
          handleClose={() => setSelectedApplication(null)}
          application={selectedApplication}
          searchValue={searchValue}
          updateRating={updateRating}
          defaultOnboarding={defaultOnboarding}
          setApplication={setSelectedApplication}
          setApplicationData={setApplicationData}
          handlePageNo={(newPageNo) => {
            setTriggerChange && setTriggerChange(true);
            handlePageNo(newPageNo);
          }}
        />
      )}
      {isLoading && pageNo !== 1 ? (
        <Box textAlign="center">
          <CircularProgress data-testid="loader" color="success" />
        </Box>
      ) : (!applicationData?.length && !isLoading) ? (
        <>
          <Alert
            severity="info"
            style={{ alignSelf: 'center', marginTop: '0' }}
          >
            {(isFilterSelected || searchValue.length > 2) &&
            authData.user?.company
              ? 'There are no applications with the selected options.'
              : 'When you receive new applications, you’ll see them here.'}
          </Alert>
          {!isFilterSelected &&
            searchValue.length <= 2 &&
            authData.user?.company && (
              <Box textAlign="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/employer/positions/')}
                >
                  Manage My Jobs
                </Button>
              </Box>
            )}
        </>
      ) : null}
    </Stack>
  );
};

export default ApplicationsList;
